import { UserAPI } from "@shared/types";
import { bridge, BridgeChannels, BridgeActions } from "../bridge";
import type { TrackingCore } from "./core";

/**
 * Booking + checkout funnel events: city/event/neighborhood selection, the
 * payment modal, purchase/checkout, plus the smaller engagement events that
 * hang off a reservation (poll votes, cancellation dialog, calendar links).
 * Merged onto the tracking singleton in index.ts.
 */
export const bookingEvents = {
  trackCalendarLinkClicked(this: TrackingCore, provider: string) {
    this.track("calendar_link_clicked", {
      provider,
    });
  },

  // ─── Booking flow ─────────────────────────────────────────────────────────
  bookingCitySelected(this: TrackingCore, cityId: string, cityName: string) {
    this.track("booking_city_selected", {
      city_id: cityId,
      city_name: cityName,
    });
  },

  bookingEventSelected(this: TrackingCore, eventId: string) {
    this.track("booking_event_selected", {
      event_id: eventId,
    });
  },

  bookingNeighborhoodSelected(this: TrackingCore, neighborhoodId: string, neighborhoodName: string) {
    this.track("booking_neighborhood_selected", {
      neighborhood_id: neighborhoodId,
      neighborhood_name: neighborhoodName,
    });
  },

  bookingContinueClicked(this: TrackingCore) {
    this.track("booking_continue_clicked");
  },

  // ─── Payment ──────────────────────────────────────────────────────────────
  paymentFormViewed(this: TrackingCore, amount: number, currency: string) {
    this.track("payment_form_viewed", {
      amount,
      currency,
    });
  },

  // Fired when the user picks subscription vs one_time in the PaymentModal toggle.
  // `variant` carries which checkout surface emitted it ("v1" legacy, "v2" sell sheet).
  paymentTypeSelected(this: TrackingCore, selectedType: "subscription" | "one_time", variant: string) {
    this.track("payment_type_selected", {
      selected_type: selectedType,
      payment_toggle_variant: variant,
    });
  },

  paymentError(this: TrackingCore, errorMessage: string, paymentMethod: string) {
    this.track("payment_error", {
      error_message: errorMessage,
      payment_method: paymentMethod,
    });
  },

  initiateCheckout(this: TrackingCore) {
    this.track("initiate_checkout");

    // Fire native Facebook InitiateCheckout
    if (bridge.isNative) {
      bridge.emit(
        BridgeChannels.TRACKING,
        BridgeActions.TRACK_INITIATE_CHECKOUT,
        {
          contentType: "dinner_reservation",
          currency: "USD",
        }
      );
    }
  },

  /**
   * @param opts.viaNativeStripe When true, the purchase went through the native
   *   Stripe Payment Sheet, so the server fires the AppsFlyer purchase via S2S
   *   (services/appsflyer.ts) from the Stripe webhook. We skip the client-side
   *   native AppsFlyer event here to avoid double-counting revenue. RevenueCat
   *   IAP purchases (and web) keep the client-side event.
   */
  purchase(
    this: TrackingCore,
    paymentOption: UserAPI.PaymentOption,
    purchaseId: string,
    opts?: { viaNativeStripe?: boolean }
  ) {
    this.track("purchase", {
      currency: paymentOption.currency ?? "USD",
      value: paymentOption.price / 100,
      transaction_id: purchaseId,
      coupon: paymentOption.couponId,
      item_name: paymentOption?.name,
      item_id: paymentOption?.id,
    });

    // Fire native AppsFlyer purchase event — unless the server will report it via
    // S2S for native Stripe (avoids double counting).
    if (bridge.isNative && !opts?.viaNativeStripe) {
      bridge.emit(BridgeChannels.TRACKING, BridgeActions.TRACK_PURCHASE, {
        amount: paymentOption.price / 100,
        currency: paymentOption.currency ?? "USD",
        parameters: {
          fb_content_type: "dinner_reservation",
          fb_content_id: paymentOption?.id,
          fb_num_items: "1",
          fb_order_id: purchaseId,
        },
      });
    }
  },

  // ─── Reservation engagement ───────────────────────────────────────────────
  pollVote(this: TrackingCore, pollId: string) {
    this.track("poll_vote", {
      poll_id: pollId,
    });
  },

  cancelDialogOpened(this: TrackingCore, reservationId: string, withinCancellationPeriod: boolean) {
    this.track("cancel_dialog_opened", {
      reservation_id: reservationId,
      within_cancellation_period: withinCancellationPeriod,
    });
  },

  cancelDialogConfirmed(this: TrackingCore, reservationId: string, withinCancellationPeriod: boolean) {
    this.track("cancel_dialog_confirmed", {
      reservation_id: reservationId,
      within_cancellation_period: withinCancellationPeriod,
    });
  },
};
